import React, { useState, useEffect } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../../../backend/convex/_generated/api';
import type { Id, Doc } from '../../../../../backend/convex/_generated/dataModel';

interface AssessmentFormProps {
  initialData?: any;
  workspaceId: Id<"workspaces">;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

export const AssessmentForm: React.FC<AssessmentFormProps> = ({
  initialData,
  workspaceId,
  onSubmit,
  onCancel,
  isSubmitting
}) => {
  const problems = useQuery(api.problems.list, { workspaceId });
  
  const [formData, setFormData] = useState({
    title: '',
    allowedSubmissions: 3,
    timeLimitMinutes: 60,
    problemSets: [] as string[],
    gradingMode: 'individual' as 'individual' | 'batch',
    settings: {
      isFullscreenEnforced: false,
      isCopyPasteDisabled: false,
    }
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        allowedSubmissions: initialData.allowedSubmissions ?? 3,
        timeLimitMinutes: initialData.timeLimitMinutes ?? 60,
        problemSets: initialData.problemSets || [],
        gradingMode: initialData.gradingMode || 'individual',
        settings: initialData.settings || {
          isFullscreenEnforced: false,
          isCopyPasteDisabled: false,
        }
      });
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const toggleProblem = (problemId: string) => {
    setFormData(prev => ({
      ...prev,
      problemSets: prev.problemSets.includes(problemId)
        ? prev.problemSets.filter(id => id !== problemId)
        : [...prev.problemSets, problemId]
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="bg-card p-8 rounded-xl border border-border flex flex-col gap-8 shadow-sm">
      <div className="flex justify-between items-center border-b border-border pb-6">
        <div>
          <h3 className="text-xl font-bold">{initialData ? 'Edit Assessment' : 'Create New Assessment'}</h3>
          <p className="text-sm text-muted-foreground mt-1">Configure the assessment title, problems, and candidate constraints.</p>
        </div>
        <div className="flex gap-3">
          <button 
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-semibold bg-transparent border border-border rounded-lg hover:bg-accent transition-colors"
          >
            Cancel
          </button>
          <button 
            type="submit"
            disabled={isSubmitting || formData.problemSets.length === 0 || !formData.title}
            className="px-6 py-2 text-sm font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : initialData ? 'Update Assessment' : 'Create Assessment'}
          </button>
        </div>
      </div>
      
      <div className="flex flex-col gap-2">
        <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Assessment Title <span className="text-destructive">*</span>
        </label>
        <input 
          required
          type="text" 
          value={formData.title}
          onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="e.g., Senior Backend Engineer - Round 1"
          className="p-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 outline-none transition-all"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Attempts Allowed</label>
          <input 
            type="number" 
            value={formData.allowedSubmissions}
            onChange={e => setFormData(prev => ({ ...prev, allowedSubmissions: parseInt(e.target.value) }))}
            className="p-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 outline-none transition-all"
          />
          <p className="text-[10px] text-muted-foreground italic">Number of times a candidate can submit. Use -1 for unlimited.</p>
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Time Limit (Minutes)</label>
          <input 
            type="number" 
            value={formData.timeLimitMinutes}
            onChange={e => setFormData(prev => ({ ...prev, timeLimitMinutes: parseInt(e.target.value) }))}
            className="p-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 outline-none transition-all"
          />
          <p className="text-[10px] text-muted-foreground italic">Total time the candidate has to complete all problems.</p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-end">
          <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Select Problems <span className="text-destructive">*</span>
          </label>
          <span className="text-[10px] font-bold bg-primary/10 text-primary px-2 py-0.5 rounded-full uppercase tracking-wider">
            {formData.problemSets.length} Selected
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[300px] overflow-y-auto p-4 border border-border rounded-xl bg-muted/30">
          {!problems ? (
            <div className="col-span-2 py-8 text-center text-sm text-muted-foreground italic flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
              Loading workspace problems...
            </div>
          ) : problems.length === 0 ? (
            <div className="col-span-2 py-8 text-center text-sm text-muted-foreground italic">
              No problems found in this workspace. Create problems first.
            </div>
          ) : (
            problems.map((p: Doc<"problems">) => (
              <label 
                key={p._id} 
                className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                  formData.problemSets.includes(p.id) 
                    ? 'bg-primary/5 border-primary shadow-sm' 
                    : 'bg-background border-border hover:border-primary/30'
                }`}
              >
                <div className="mt-1">
                  <input 
                    type="checkbox" 
                    checked={formData.problemSets.includes(p.id)}
                    onChange={() => toggleProblem(p.id)}
                    className="accent-primary w-4 h-4"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">{p.title}</span>
                  <div className="flex gap-2 mt-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{p.difficulty}</span>
                    <span className="text-[10px] text-muted-foreground/60">•</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{p.id}</span>
                  </div>
                </div>
              </label>
            ))
          )}
        </div>
        {formData.problemSets.length === 0 && (
          <p className="text-xs text-destructive font-medium">Please select at least one problem for this assessment.</p>
        )}
      </div>

      <div className="flex flex-col gap-4 p-6 bg-muted/50 border border-border rounded-xl">
        <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-1">Candidate Guardrails</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <label className="flex items-center gap-3 cursor-pointer group">
            <div className="relative flex items-center justify-center">
              <input 
                type="checkbox" 
                checked={formData.settings.isFullscreenEnforced}
                onChange={e => setFormData(prev => ({ 
                  ...prev, 
                  settings: { ...prev.settings, isFullscreenEnforced: e.target.checked } 
                }))}
                className="w-5 h-5 accent-primary rounded cursor-pointer transition-all"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold group-hover:text-primary transition-colors">Enforce Fullscreen</span>
              <span className="text-[10px] text-muted-foreground">Alert and log when a candidate exits fullscreen mode.</span>
            </div>
          </label>
          <label className="flex items-center gap-3 cursor-pointer group">
            <div className="relative flex items-center justify-center">
              <input 
                type="checkbox" 
                checked={formData.settings.isCopyPasteDisabled}
                onChange={e => setFormData(prev => ({ 
                  ...prev, 
                  settings: { ...prev.settings, isCopyPasteDisabled: e.target.checked } 
                }))}
                className="w-5 h-5 accent-primary rounded cursor-pointer transition-all"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold group-hover:text-primary transition-colors">Disable Copy/Paste</span>
              <span className="text-[10px] text-muted-foreground">Prevent candidates from pasting external code into the editor.</span>
            </div>
          </label>
        </div>
      </div>

      <div className="flex gap-4 justify-end border-t border-border pt-8">
        <button 
          type="button"
          onClick={onCancel}
          className="px-6 py-2.5 text-sm font-semibold bg-transparent border border-border rounded-lg hover:bg-accent transition-colors"
        >
          Cancel
        </button>
        <button 
          type="submit"
          disabled={isSubmitting || formData.problemSets.length === 0 || !formData.title}
          className="px-10 py-2.5 text-sm font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving...' : initialData ? 'Update Assessment' : 'Create Assessment'}
        </button>
      </div>
    </form>
  );
};

